import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { GeoJSON, MapContainer, TileLayer } from 'react-leaflet';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  BarElement
} from 'chart.js';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

interface WalkabilityTractSectionProps {
  location: string;
  geofips: number | null;
}

type WalkClass =
  | 'Highly walkable (80–100)'
  | 'Above average (60–80)'
  | 'Moderate (40–60)'
  | 'Below average (20–40)'
  | 'Least walkable (0–20)'
  | 'Unknown';

const CLASS_ORDER: WalkClass[] = [
  'Highly walkable (80–100)',
  'Above average (60–80)',
  'Moderate (40–60)',
  'Below average (20–40)',
  'Least walkable (0–20)',
  'Unknown'
];

const CLASS_COLORS: Record<WalkClass, string> = {
  'Highly walkable (80–100)': '#15803d',
  'Above average (60–80)': '#22c55e',
  'Moderate (40–60)': '#eab308',
  'Below average (20–40)': '#f97316',
  'Least walkable (0–20)': '#b91c1c',
  Unknown: '#94a3b8'
};

function normalizeTractGeofips(value: string | number | null | undefined): string {
  if (value == null) return '';
  const n = typeof value === 'number' ? value : Number(String(value).replace(/\D/g, ''));
  if (Number.isNaN(n)) return String(value).trim();
  return String(Math.trunc(n));
}

/** 5-digit state+county prefix for CA tract GEOIDs (e.g. 06001). */
function countyGeofipsToTractPrefix(countyGeofips: number | null): string | null {
  if (countyGeofips == null || Number.isNaN(countyGeofips)) return null;
  const n = Math.trunc(Math.abs(countyGeofips));
  const s = String(n);
  if (s.length <= 5) return s.padStart(5, '0');
  return s.slice(0, 5);
}

function classifyFromPercentile(p: number | null | undefined): WalkClass {
  if (p == null || Number.isNaN(Number(p))) return 'Unknown';
  const v = Number(p);
  if (v >= 80) return 'Highly walkable (80–100)';
  if (v >= 60) return 'Above average (60–80)';
  if (v >= 40) return 'Moderate (40–60)';
  if (v >= 20) return 'Below average (20–40)';
  return 'Least walkable (0–20)';
}

function pickPercentile(row: Record<string, unknown>): number | null {
  const keys = Object.keys(row);
  const preferred = keys.find((k) => /nat_walk.*percentile/i.test(k));
  const key = preferred || keys.find((k) => /walk.*percentile/i.test(k));
  if (!key) return null;
  const v = row[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

async function fetchCountyWalkabilityRows(countyPrefix: string): Promise<Record<string, unknown>[]> {
  const pageSize = 1000;
  let offset = 0;
  const all: Record<string, unknown>[] = [];
  const pad = countyPrefix.padStart(5, '0');

  while (true) {
    const { data, error } = await supabaseClient
      .from('walkability_tract')
      .select('*')
      .like('geofips', `${pad}%`)
      .range(offset, offset + pageSize - 1);

    if (error) {
      const { data: alt, error: err2 } = await supabaseClient
        .from('walkability_tract')
        .select('*')
        .filter('geofips', 'gte', `${pad}00000`)
        .filter('geofips', 'lte', `${pad}99999`)
        .range(offset, offset + pageSize - 1);
      if (err2) throw error;
      const rows = (alt || []) as Record<string, unknown>[];
      all.push(...rows);
      if (rows.length < pageSize) break;
      offset += pageSize;
      continue;
    }

    const rows = (data || []) as Record<string, unknown>[];
    all.push(...rows);
    if (rows.length < pageSize) break;
    offset += pageSize;
  }

  if (all.length > 0) return all;

  const { data: fallback, error: fbErr } = await supabaseClient.from('walkability_tract').select('*').limit(5000);
  if (fbErr) throw fbErr;
  const rows = (fallback || []) as Record<string, unknown>[];
  return rows.filter((r) => {
    const g = normalizeTractGeofips(r.geofips as string | number | undefined);
    const g11 = g.length === 11 ? g : g.padStart(11, '0');
    return g11.startsWith(pad);
  });
}

async function fetchPlaceTractSet(placeGeofips: string): Promise<Set<string>> {
  const tractCols = ['trct', 'tract_geofips'];
  const placeCols = ['stplc', 'place_geofips'];
  const out = new Set<string>();

  for (const tc of tractCols) {
    for (const pc of placeCols) {
      const { data, error } = await supabaseClient
        .from('relationship_trct_place_20')
        .select(`${tc}, ${pc}`)
        .eq(pc, placeGeofips);
      if (error) continue;
      for (const row of data || []) {
        const tr = (row as any)[tc];
        const id = normalizeTractGeofips(tr);
        if (id) out.add(id.length === 11 ? id : id.padStart(11, '0'));
      }
      if (out.size > 0) return out;
    }
  }
  return out;
}

async function resolveCountyPrefixForLocation(
  location: string
): Promise<{ countyPrefix: string | null; scope: 'city' | 'county' | 'state' | 'unknown'; placeGeofips: string | null }> {
  await supabaseLocationService.initialize();
  const trimmed = location.trim();
  if (!trimmed) return { countyPrefix: null, scope: 'unknown', placeGeofips: null };

  if (trimmed.toLowerCase() === 'california') {
    return { countyPrefix: null, scope: 'state', placeGeofips: null };
  }

  const locType = supabaseLocationService.getLocationType(trimmed);

  if (locType === 'county') {
    const gf = await supabaseLocationService.getGeofips(trimmed);
    return { countyPrefix: countyGeofipsToTractPrefix(gf), scope: 'county', placeGeofips: null };
  }

  if (locType === 'city') {
    const placeGf = await supabaseLocationService.getGeofips(trimmed);
    const countyName = supabaseLocationService.getCountyForCity(trimmed);
    if (!countyName) {
      return { countyPrefix: null, scope: 'city', placeGeofips: placeGf != null ? String(placeGf) : null };
    }
    const { data: countyRow } = await supabaseClient
      .from('population')
      .select('geofips')
      .eq('geography_type', 'County')
      .ilike('location_name', `${countyName}%`)
      .limit(1)
      .maybeSingle();
    const countyGf = countyRow?.geofips != null ? Number(countyRow.geofips) : null;
    return {
      countyPrefix: countyGeofipsToTractPrefix(countyGf),
      scope: 'city',
      placeGeofips: placeGf != null ? String(placeGf) : null
    };
  }

  const gf = await supabaseLocationService.getGeofips(trimmed);
  const prefix = countyGeofipsToTractPrefix(gf);
  return { countyPrefix: prefix, scope: 'unknown', placeGeofips: gf != null ? String(gf) : null };
}

const WalkabilityTractSection: React.FC<WalkabilityTractSectionProps> = ({ location, geofips }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countyRows, setCountyRows] = useState<Record<string, unknown>[]>([]);
  const [placeTractIds, setPlaceTractIds] = useState<Set<string> | null>(null);
  const [scopeLabel, setScopeLabel] = useState<string>('');
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([36.7783, -119.4179]);
  const [mapZoom, setMapZoom] = useState(8);
  const mapFittedRef = useRef(false);

  const walkByGeoid = useMemo(() => {
    const m = new Map<string, { percentile: number | null; klass: WalkClass }>();
    for (const row of countyRows) {
      const raw = row.geofips ?? (row as any).tract ?? (row as any).geoid;
      const g = normalizeTractGeofips(raw as string | number);
      const geoid = g.length === 11 ? g : g.padStart(11, '0');
      const pct = pickPercentile(row);
      m.set(geoid, { percentile: pct, klass: classifyFromPercentile(pct) });
    }
    return m;
  }, [countyRows]);

  useEffect(() => {
    const loadGeo = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/california_tracts_optimized.geojson`);
        const data = await response.json();
        setGeoJsonData(data);
      } catch (e) {
        console.warn('WalkabilityTractSection: geojson load failed', e);
      }
    };
    loadGeo();
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      setCountyRows([]);
      setPlaceTractIds(null);
      setScopeLabel('');

      try {
        const { countyPrefix, scope, placeGeofips } = await resolveCountyPrefixForLocation(location);

        if (scope === 'state') {
          setError('Select a city or county to view tract walkability.');
          setLoading(false);
          return;
        }

        if (!countyPrefix) {
          setError('Could not resolve county for this location.');
          setLoading(false);
          return;
        }

        const rows = await fetchCountyWalkabilityRows(countyPrefix);
        setCountyRows(rows);

        const locType = supabaseLocationService.getLocationType(location.trim());
        let tractFilter: Set<string> | null = null;
        if (locType === 'city' && placeGeofips) {
          tractFilter = await fetchPlaceTractSet(placeGeofips);
          setPlaceTractIds(tractFilter);
          setScopeLabel(`Tracts intersecting ${location.trim()} (relationship_trct_place_20)`);
        } else {
          setPlaceTractIds(null);
          setScopeLabel(`All census tracts in county (prefix ${countyPrefix})`);
        }

        if (tractFilter && tractFilter.size === 0) {
          setScopeLabel(
            `County tracts shown; place crosswalk had no rows for GEOID ${placeGeofips} — bar chart falls back to county tracts.`
          );
        }
      } catch (e) {
        console.error('WalkabilityTractSection:', e);
        setError(e instanceof Error ? e.message : 'Failed to load walkability data.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [location, geofips]);

  const barCounts = useMemo(() => {
    const counts: Record<WalkClass, number> = {
      'Highly walkable (80–100)': 0,
      'Above average (60–80)': 0,
      'Moderate (40–60)': 0,
      'Below average (20–40)': 0,
      'Least walkable (0–20)': 0,
      Unknown: 0
    };

    const usePlace = placeTractIds && placeTractIds.size > 0;

    for (const row of countyRows) {
      const raw = row.geofips ?? (row as any).tract ?? (row as any).geoid;
      const g = normalizeTractGeofips(raw as string | number);
      const geoid = g.length === 11 ? g : g.padStart(11, '0');
      if (usePlace && !placeTractIds!.has(geoid)) continue;
      const pct = pickPercentile(row);
      const k = classifyFromPercentile(pct);
      counts[k] += 1;
    }

    if (usePlace) {
      const sum = Object.values(counts).reduce((a, b) => a + b, 0);
      if (sum === 0) {
        for (const row of countyRows) {
          const raw = row.geofips ?? (row as any).tract ?? (row as any).geoid;
          const pct = pickPercentile(row);
          const k = classifyFromPercentile(pct);
          counts[k] += 1;
        }
      }
    }

    return counts;
  }, [countyRows, placeTractIds]);

  const barData = useMemo(
    () => ({
      labels: CLASS_ORDER.filter((c) => c !== 'Unknown' || barCounts.Unknown > 0),
      datasets: [
        {
          label: 'Tract count',
          data: CLASS_ORDER.filter((c) => c !== 'Unknown' || barCounts.Unknown > 0).map((c) => barCounts[c]),
          backgroundColor: CLASS_ORDER.filter((c) => c !== 'Unknown' || barCounts.Unknown > 0).map((c) => CLASS_COLORS[c])
        }
      ]
    }),
    [barCounts]
  );

  const onEachFeature = useCallback(
    (feature: any, layer: L.Layer) => {
      const geoid = String(feature?.properties?.GEOID || '').padStart(11, '0');
      const w = walkByGeoid.get(geoid);
      if (!w) return;
      layer.bindPopup(
        `<div style="font-size:12px"><strong>GEOID ${geoid}</strong><br/>` +
          (w.percentile != null ? `Percentile: ${w.percentile.toFixed(1)}<br/>` : '') +
          `${w.klass}</div>`
      );
    },
    [walkByGeoid]
  );

  const geoJsonStyle = useCallback(
    (feature: any) => {
      const geoid = String(feature?.properties?.GEOID || '').padStart(11, '0');
      const w = walkByGeoid.get(geoid);
      const k = w?.klass ?? 'Unknown';
      return {
        fillColor: CLASS_COLORS[k],
        weight: 0.5,
        color: '#1e293b',
        fillOpacity: 0.82
      };
    },
    [walkByGeoid]
  );

  const filteredGeoJson = useMemo(() => {
    if (!geoJsonData?.features?.length || walkByGeoid.size === 0) return null;
    const keys = new Set(walkByGeoid.keys());
    const features = geoJsonData.features.filter((f: any) => {
      const geoid = String(f?.properties?.GEOID || '').padStart(11, '0');
      return keys.has(geoid);
    });
    if (features.length === 0) return null;
    return { type: 'FeatureCollection' as const, features };
  }, [geoJsonData, walkByGeoid]);

  useEffect(() => {
    mapFittedRef.current = false;
  }, [location, walkByGeoid]);

  useEffect(() => {
    if (!filteredGeoJson?.features?.length || mapFittedRef.current) return;
    const first = filteredGeoJson.features[0] as any;
    const lat = first?.properties?.INTPTLAT != null ? parseFloat(String(first.properties.INTPTLAT)) : NaN;
    const lon = first?.properties?.INTPTLON != null ? parseFloat(String(first.properties.INTPTLON)) : NaN;
    if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
      setMapCenter([lat, lon]);
      setMapZoom(10);
      mapFittedRef.current = true;
    }
  }, [filteredGeoJson]);

  if (loading) {
    return (
      <div style={{ padding: '1rem', color: '#64748b' }}>
        Loading EPA National Walkability Index (tract)…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', color: '#b91c1c' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {scopeLabel ? (
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569' }}>{scopeLabel}</p>
      ) : null}

      <div style={{ height: 380, width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        {filteredGeoJson ? (
          <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <GeoJSON data={filteredGeoJson} style={geoJsonStyle as any} onEachFeature={onEachFeature} />
          </MapContainer>
        ) : (
          <div style={{ padding: '1rem', color: '#64748b' }}>
            {countyRows.length === 0
              ? 'No rows in walkability_tract for this county (check Supabase table and RLS).'
              : 'Could not match tracts to GeoJSON (GEOID).'}
          </div>
        )}
      </div>

      <div style={{ maxWidth: 720 }}>
        <Bar
          data={barData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: false },
              title: { display: true, text: 'Tracts by walkability class (CA percentile buckets)' }
            },
            scales: {
              x: { ticks: { maxRotation: 45, minRotation: 35, font: { size: 10 } } },
              y: { beginAtZero: true, ticks: { precision: 0 } }
            }
          }}
        />
      </div>
    </div>
  );
};

export default WalkabilityTractSection;
